import { useQuery } from '@apollo/client';
import type { Colour } from '@risksmart-app/components/src/utils/colours';
import {
  colours,
  isColorKey,
} from '@risksmart-app/components/src/utils/colours';
import i18n from '@risksmart-app/i18n/src/i18n';
import type { RiskRegisterResponseRow } from '@risksmart-app/trpc/src/types';
import type {
  Risk_Assessment_Result_Bool_Exp,
  Risk_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetAppetitesGroupedByImpactDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetRisksFlatDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { UseQueryOptions } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { mapTrpcRisksToGraphQL } from 'src/hooks/queries/risk/useGetRisksFlat';
import type { RiskRegisterFields } from 'src/pages/risks/types';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';
import { useRiskScores } from '@/hooks/useRiskScore';
import type { ResolvedRating } from '@/ratings/useRiskRatingResolver';
import { riskRegisterUrl } from '@/utils/urls';

import { useGetRiskSmartWidgetTableProps } from '../../../risks/config';
import { UNRATED } from '../../gigawidget/types';
import {
  dateRangeFilter,
  departmentsFilter,
  tagsFilter,
} from '../../gigawidget/util/filterHelpers';
import { getQueryVariables } from '../../my-items/hooks/useGetQueryVariables';
import { createDataSource } from '../createDataSource';
import {
  dashboardDateRangeClickthroughFilter,
  dateRangeClickthroughFilter,
  defaultClickthroughFilter,
  defaultClickthroughFilterWithUnrated,
  tagAndDepartmentCategoryGetters,
} from '../dataSourceHelpers';

// Shared title/colour override for the rating-distribution getters, which only
// surface a band when the score is on the current config version (else Unrated).
const ratingCategoryOverride = (rating: ResolvedRating | undefined) => ({
  title: rating?.label ?? UNRATED,
  color:
    colours[(rating?.color ?? 'light-grey') as Colour]?.backgroundColor ??
    rating?.color,
});

export default createDataSource({
  group: 'single',
  hasAccess: () => true,
  parentTypes: [
    Parent_Type_Enum.Risk,
    Parent_Type_Enum.RiskControlledAssessment,
    Parent_Type_Enum.RiskUncontrolledAssessment,
  ],
  documentNode: GetRisksFlatDocument,
  trpcQuery: (trpc) => {
    const queryOptions = trpc.frontend.risk.register.queryOptions();

    // Use select to transform the data and handle the response properly
    const transformedQueryOptions = {
      ...queryOptions,
      select: (data: RiskRegisterResponseRow[]) => {
        return mapTrpcRisksToGraphQL(data);
      },
    };

    return transformedQueryOptions as UseQueryOptions<unknown, Error>;
  },
  useDefaultVariables: () =>
    ({
      where: useEntityWhereFilter<Risk_Bool_Exp>(Parent_Type_Enum.Risk),
      riskAssessmentResultsWhere: {},
    }) as {
      where: Risk_Bool_Exp;
      riskAssessmentResultsWhere: Risk_Assessment_Result_Bool_Exp;
    },
  useTablePropsHook: (data, options) => {
    const { scores, loading: scoresLoading } = useRiskScores();

    const { data: impactAppetites, loading: appetitesLoading } = useQuery(
      GetAppetitesGroupedByImpactDocument
    );

    const tableProps = useGetRiskSmartWidgetTableProps(
      data?.risk,
      scores,
      impactAppetites?.impact,
      options
    );

    // Rows exist as soon as the register query lands, but score/appetite
    // fields stay undefined until these secondary sources arrive — saved
    // filter tokens on those fields would match nothing (RSP-6539).
    return {
      ...tableProps,
      isEnrichmentLoading: scoresLoading || appetitesLoading,
    };
  },
  entityNamePlural: 'risk_other',
  entityNameSingular: 'risk_one',
  fields: 'risks.fields',
  dashboardFilterConfig: {
    tagsFilter: (tags) => ({ where: { tags: tagsFilter(tags) } }),
    departmentsFilter: (departments) => ({
      where: { departments: departmentsFilter(departments) },
    }),
    dateFilter: (dateRange, precision) => ({
      where: { CreatedAtTimestamp: dateRangeFilter(dateRange, precision) },
    }),
    dateClickthroughFilter:
      dashboardDateRangeClickthroughFilter('CreatedAtTimestamp'),
    ownershipFilter: (myItemsFilters, userId) => ({
      where: getQueryVariables(myItemsFilters, userId).riskFilterConditions,
    }),
  },
  clickThroughUrl: (filter) => riskRegisterUrl(filter),
  categoryGetters: [
    ...tagAndDepartmentCategoryGetters<RiskRegisterFields>(),
    {
      id: 'tier',
      name: () => i18n.t('risks.columns.risk_tier'),
      categoryGetter: (item) => item.TierLabelled,
      clickthroughFilter: defaultClickthroughFilter('TierLabelled'),
    },
    {
      id: 't1Category',
      name: () => i18n.t('risks.columns.t1_category'),
      categoryGetter: (item) => item.T1CategoryTitle ?? UNRATED,
      clickthroughFilter: defaultClickthroughFilter('T1CategoryTitle'),
    },
    {
      id: 't2Category',
      name: () => i18n.t('risks.columns.t2_category'),
      categoryGetter: (item) => item.T2CategoryTitle ?? UNRATED,
      clickthroughFilter: defaultClickthroughFilter('T2CategoryTitle'),
    },
    {
      id: 'status',
      name: () => i18n.t('risks.columns.risk_status'),
      categoryGetter: (item) => item.StatusLabelled ?? UNRATED,
      clickthroughFilter: defaultClickthroughFilter('StatusLabelled', {
        unratedValue: '',
      }),
    },
    {
      id: 'treatment',
      name: () => i18n.t('risks.columns.risk_treatment'),
      categoryGetter: (item) => item.TreatmentLabelled ?? UNRATED,
      clickthroughFilter: defaultClickthroughFilter('TreatmentLabelled'),
    },
    {
      id: 'owner',
      name: () => i18n.t('risks.columns.risk_owner'),
      categoryGetter: (item) =>
        item.allOwners.map((owner) => ({ key: owner.id, label: owner.label })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'allOwners',
          operator: '=',
          value: category.key,
        },
      ],
    },
    {
      id: 'contributor',
      name: () => i18n.t(`risks.columns.risk_contributor`),
      categoryGetter: (item) =>
        item.allContributors.map((contributor) => ({
          key: contributor.id,
          label: contributor.label,
        })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'allContributors',
          operator: '=',
          value: category.key,
        },
      ],
    },
    {
      id: 'entity',
      name: () => i18n.t('risks.columns.entity'),
      categoryGetter: (item) => item.Entity ?? UNRATED,
      clickthroughFilter: defaultClickthroughFilter('Entity'),
    },
    {
      id: 'enterpriseRisk',
      name: () => i18n.t('risks.columns.enterprise_risk'),
      categoryGetter: (item) => item.EnterpriseRiskLabelled ?? UNRATED,
      clickthroughFilter: defaultClickthroughFilter('EnterpriseRiskLabelled'),
    },
    {
      // RSP-6367: group risks by their linked Parent Enterprise Risk NAME.
      // Unlinked risks (no enterprise-risk instance) bucket under a "–" label.
      id: 'parentEnterpriseRisk',
      name: () => i18n.t('risks.columns.parent_enterprise_risk'),
      categoryGetter: (item) =>
        item.ParentEnterpriseRiskTitle
          ? {
              key: item.ParentEnterpriseRiskTitle,
              label: item.ParentEnterpriseRiskTitle,
            }
          : {
              key: UNRATED,
              label: i18n.t('risks.columns.parent_enterprise_risk_none'),
            },
      // Linked groups filter on the real ParentEnterpriseRiskTitle (category.key);
      // the unlinked bucket targets an empty title so the clickthrough matches
      // risks with no Parent Enterprise Risk.
      clickthroughFilter: defaultClickthroughFilterWithUnrated(
        'ParentEnterpriseRiskTitle',
        {
          categoryValue: 'key',
          unratedValue: '',
        }
      ),
    },
    {
      // RSP-6367: group risks by their linked Parent Enterprise Risk friendly
      // ID (ER-###). Unlinked risks bucket under a "–" label.
      id: 'parentEnterpriseRiskId',
      name: () => i18n.t('risks.columns.parent_enterprise_risk_id'),
      categoryGetter: (item) =>
        item.ParentEnterpriseRiskId
          ? {
              key: item.ParentEnterpriseRiskId,
              label: item.ParentEnterpriseRiskId,
            }
          : {
              key: UNRATED,
              label: i18n.t('risks.columns.parent_enterprise_risk_none'),
            },
      clickthroughFilter: defaultClickthroughFilterWithUnrated(
        'ParentEnterpriseRiskId',
        {
          categoryValue: 'key',
          unratedValue: '',
        }
      ),
    },
    {
      id: 'parentRisk',
      name: () => i18n.t('risks.columns.parent_risk'),
      categoryGetter: (item) =>
        item.ParentTitle
          ? { key: item.ParentTitle, label: item.ParentTitle }
          : {
              key: UNRATED,
              label: i18n.t('risks.columns.parent_risk_none'),
            },
      // Use the row's actual ParentTitle (category.key) so the clickthrough
      // filter targets the real value rather than the translated picker
      // label. For risks with no parent, the unratedValue is empty so the
      // filter matches against an empty ParentTitle.
      clickthroughFilter: defaultClickthroughFilterWithUnrated('ParentTitle', {
        categoryValue: 'key',
        unratedValue: '',
      }),
    },
    {
      id: 'controlledRating',
      name: () => i18n.t('risks.columns.controlled_rating'),
      // Only counts ratings on the current scoring config version; scores on an
      // older version resolve to Unrated via the *LatestOnly option (built in
      // useGetLabelledFields). The register keeps the version-resolved band.
      categoryGetter: (item) => ({
        key: item.ControlledRatingOptionLatestOnly?.label ?? UNRATED,
        label: item.ControlledRatingOptionLatestOnly?.label ?? UNRATED,
        sortKey: item.ControlledRatingOptionLatestOnly?.value?.toString(),
      }),
      ratingColourKey: 'risk_controlled',
      clickthroughFilter: defaultClickthroughFilter(
        'ControlledRatingLabelled',
        {
          unratedValue: UNRATED,
        }
      ),
      categoryOverrideFunction: (category) =>
        ratingCategoryOverride(
          category.data[0]?.ControlledRatingOptionLatestOnly
        ),
    },
    {
      id: 'uncontrolledRating',
      name: () => i18n.t('risks.columns.uncontrolled_rating'),
      // Only counts ratings on the current scoring config version; scores on an
      // older version resolve to Unrated via the *LatestOnly option (built in
      // useGetLabelledFields). The register keeps the version-resolved band.
      categoryGetter: (item) => ({
        key: item.UncontrolledRatingOptionLatestOnly?.label ?? UNRATED,
        label: item.UncontrolledRatingOptionLatestOnly?.label ?? UNRATED,
        sortKey: item.UncontrolledRatingOptionLatestOnly?.value?.toString(),
      }),
      ratingColourKey: 'risk_uncontrolled',
      clickthroughFilter: defaultClickthroughFilter(
        'UncontrolledRatingLabelled',
        {
          unratedValue: UNRATED,
        }
      ),
      categoryOverrideFunction: (category) =>
        ratingCategoryOverride(
          category.data[0]?.UncontrolledRatingOptionLatestOnly
        ),
    },
    {
      id: 'controlledLikelihood',
      name: () => i18n.t('risks.columns.controlled_likelihood'),
      categoryGetter: (item) => ({
        key: item.ControlledLikelihood || UNRATED,
        label: item.ControlledLikelihood || UNRATED,
        sortKey: item.ControlledLikelihoodValue?.toString(),
      }),
      clickthroughFilter: defaultClickthroughFilterWithUnrated(
        'ControlledLikelihood',
        {
          unratedValue: '',
        }
      ),
      categoryOverrideFunction: (category) => {
        const colour = category.data[0]?.ControlledLikelihoodColour;
        const key = colour ?? 'light-grey';

        return {
          title: category.data[0]?.ControlledLikelihood || UNRATED,
          color: isColorKey(key)
            ? colours[key].backgroundColor
            : (colour ?? undefined),
        };
      },
    },
    {
      id: 'uncontrolledLikelihood',
      name: () => i18n.t('risks.columns.uncontrolled_likelihood'),
      categoryGetter: (item) => ({
        key: item.UncontrolledLikelihood || UNRATED,
        label: item.UncontrolledLikelihood || UNRATED,
        sortKey: item.UncontrolledLikelihoodValue?.toString(),
      }),
      clickthroughFilter: defaultClickthroughFilterWithUnrated(
        'UncontrolledLikelihood',
        {
          unratedValue: '',
        }
      ),
      categoryOverrideFunction: (category) => {
        const colour = category.data[0]?.UncontrolledLikelihoodColour;
        const key = colour ?? 'light-grey';

        return {
          title: category.data[0]?.UncontrolledLikelihood || UNRATED,
          color: isColorKey(key)
            ? colours[key].backgroundColor
            : (colour ?? undefined),
        };
      },
    },
    {
      id: 'controlledImpact',
      name: () => i18n.t('risks.columns.controlled_impact'),
      categoryGetter: (item) => ({
        key: item.ControlledImpact || UNRATED,
        label: item.ControlledImpact || UNRATED,
        sortKey: item.ControlledImpactValue?.toString(),
      }),
      clickthroughFilter: defaultClickthroughFilterWithUnrated(
        'ControlledImpact',
        {
          unratedValue: '',
        }
      ),
      categoryOverrideFunction: (category) => {
        const colour = category.data[0]?.ControlledImpactColour;
        const key = colour ?? 'light-grey';

        return {
          title: category.data[0]?.ControlledImpact || UNRATED,
          color: isColorKey(key)
            ? colours[key].backgroundColor
            : (colour ?? undefined),
        };
      },
    },
    {
      id: 'uncontrolledImpact',
      name: () => i18n.t('risks.columns.uncontrolled_impact'),
      categoryGetter: (item) => ({
        key: item.UncontrolledImpact || UNRATED,
        label: item.UncontrolledImpact || UNRATED,
        sortKey: item.UncontrolledImpactValue?.toString(),
      }),
      clickthroughFilter: defaultClickthroughFilterWithUnrated(
        'UncontrolledImpact',
        {
          unratedValue: '',
        }
      ),
      categoryOverrideFunction: (category) => {
        const colour = category.data[0]?.UncontrolledImpactColour;
        const key = colour ?? 'light-grey';

        return {
          title: category.data[0]?.UncontrolledImpact || UNRATED,
          color: isColorKey(key)
            ? colours[key].backgroundColor
            : (colour ?? undefined),
        };
      },
    },
    {
      id: 'appetitePerformance',
      name: () => i18n.t('risks.columns.appetite_performance'),
      categoryGetter: (item) => ({
        key: item.AppetitePerformance ?? UNRATED,
        label: item.AppetitePerformanceLabelled ?? UNRATED,
        sortKey: item.AppetitePerformance?.toString(),
      }),
      ratingColourKey: 'appetite_performance',
      clickthroughFilter: defaultClickthroughFilter(
        'AppetitePerformanceLabelled',
        {
          unratedValue: UNRATED,
        }
      ),
      categoryOverrideFunction: (category, ratingFns, _) => {
        const rating = ratingFns.getByValue(
          category.data[0]?.AppetitePerformance
        );

        return {
          title: rating?.label ?? UNRATED,
          color:
            colours[(rating?.color ?? 'light-grey') as Colour]
              ?.backgroundColor ?? rating?.color,
        };
      },
    },
    {
      id: 'createdDate',
      name: () => i18n.t('columns.created_on'),
      categoryGetter: (data) => new Date(data.CreatedAtTimestamp),
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        where: { CreatedAtTimestamp: dateRangeFilter(dateRange, precision) },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('CreatedAtTimestamp'),
    },
    {
      id: 'nextTestDate',
      name: () => i18n.t('risks.columns.next_test_date'),
      categoryGetter: (data) => {
        const d = dayjs(data.NextTestDate);

        return d.isValid() ? d.toDate() : null;
      },
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        where: {
          scheduleState: { DueDate: dateRangeFilter(dateRange, precision) },
        },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('NextTestDate'),
    },
    {
      id: 'nextTestOverdueDate',
      name: () => i18n.t('risks.columns.nextTestOverdue'),
      categoryGetter: (data) => {
        const d = dayjs(data.NextTestOverdueDate);

        return d.isValid() ? d.toDate() : null;
      },
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        where: {
          scheduleState: { OverdueDate: dateRangeFilter(dateRange, precision) },
        },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('NextTestOverdueDate'),
    },
    {
      id: 'testScheduleStatus',
      name: () => i18n.t('risks.columns.testScheduleStatus'),
      categoryGetter: (item) => {
        return item.TestScheduleStatus === '-'
          ? { key: '-', label: i18n.t('columns.unscheduled') }
          : item.TestScheduleStatusLabelled;
      },
      clickthroughFilter: (category) => [
        {
          propertyKey: 'TestScheduleStatusLabelled',
          operator: '=',
          value: category.key === '-' ? '-' : category.key,
        },
      ],
    },
    {
      id: 'uncontrolledRatingTrend',
      name: () => i18n.t('risks.columns.uncontrolled_rating_trend'),
      categoryGetter: (item) => ({
        key: item.UncontrolledRatingTrend ?? UNRATED,
        label: item.UncontrolledRatingTrendLabelled ?? UNRATED,
      }),
      ratingColourKey: 'rating_trend',
      isHiddenWhenAggregationsEnabled: true,
      clickthroughFilter: defaultClickthroughFilter(
        'UncontrolledRatingTrendLabelled',
        { unratedValue: UNRATED }
      ),
    },
    {
      id: 'controlledRatingTrend',
      name: () => i18n.t('risks.columns.controlled_rating_trend'),
      categoryGetter: (item) => ({
        key: item.ControlledRatingTrend ?? UNRATED,
        label: item.ControlledRatingTrendLabelled ?? UNRATED,
      }),
      ratingColourKey: 'rating_trend',
      isHiddenWhenAggregationsEnabled: true,
      clickthroughFilter: defaultClickthroughFilter(
        'ControlledRatingTrendLabelled',
        { unratedValue: UNRATED }
      ),
    },
  ],
});
